// Dashboard widget size model (PRD #214 / issue #215, ADR-0009).
//
// The size-aware flow grid (`WidgetGrid`) gives every pinned widget a width and
// a height in grid cells, so a one-number tile can be 1x1 and a seven-column
// table a full-width 4x3 on the same canvas. This module is the pure,
// unit-tested core of that model: the size SPECS (each widget's DEFAULT
// footprint, plus optional bounds), the free-form resize CLAMP, the static
// Tailwind span-class maps the grid renders, and the legacy-coercion helper that
// lets an old `string[]` saved layout (PRD #185) still load.
//
// Resize is FREE-FORM (edit-mode corner grip): a widget pins at its default and
// the user drags it to any footprint within the grid bounds — there is no fixed
// preset set. `clampSize` is the gate that keeps a raw drag inside
// [MIN_W..MAX_W] × [MIN_H..MAX_H] (narrowed by a spec's own `min`/`max`).
//
// Two layers of spec (KPI widget-sizing follow-up):
//   - a per-KIND fallback (`WIDGET_KIND_SPECS`) — the archetype's shape, and
//   - an optional per-WIDGET override (`LibraryWidget.size`) — e.g. a donut
//     wants a square while a trend line wants a wide rectangle, even though both
//     are `chart`s. `sizeSpecFor` folds the override over the kind fallback.
//
// No inline styles and no dynamically-built class strings: every span class is
// a full literal so Tailwind's JIT scanner keeps it in the build. The maps are
// keyed by the discrete `w`/`h` values so the grid only ever indexes them with
// a value that already exists.

/**
 * The three widget archetypes on a My Page canvas. A `kind` is declared per
 * catalogue entry (`LibraryWidget.kind`) and drives the FALLBACK size spec a
 * widget gets when it declares no per-widget `size`:
 *   - `tile`  — a single headline figure (`MetricTile`); small.
 *   - `chart` — a bars / trend / donut chart; medium, can grow wide.
 *   - `table` — a multi-column table (league / categories); always full-width.
 */
export type WidgetKind = 'tile' | 'chart' | 'table';

/** Width in grid columns (the canvas is 4 columns wide at `lg`). */
export type WidgetWidth = 1 | 2 | 3 | 4;
/** Height in grid rows (each row is `auto-rows-widget-cell` = 120px). */
export type WidgetHeight = 1 | 2 | 3 | 4 | 5 | 6;

/** A widget's footprint on the flow grid, in grid cells. */
export interface WidgetSize {
    w: WidgetWidth;
    h: WidgetHeight;
}

// Grid bounds — the single source of truth for the free-form resize clamp (and
// mirrored by the server's PUT validation). Width tops out at the 4-column track;
// height tops out at 6 rows, the widest the static span maps below cover.
export const MIN_W: WidgetWidth = 1;
export const MAX_W: WidgetWidth = 4;
export const MIN_H: WidgetHeight = 1;
export const MAX_H: WidgetHeight = 6;

// The Android-style absolute grid can place a widget at any row down to MAX_ROWS
// (the deepest the static `row-start` map + the Tailwind theme cover). A widget's
// `y` position is 1..MAX_ROWS; see `placement.ts`.
export const MAX_ROWS = 12;

/**
 * A widget's sizing rule: the DEFAULT footprint it gets when none is stored, and
 * optional per-spec `min`/`max` bounds that narrow the global grid bounds for the
 * free-form resize (e.g. a table that should never be shrunk below full width).
 */
export interface WidgetSizeSpec {
    default: WidgetSize;
    min?: WidgetSize;
    max?: WidgetSize;
}

/**
 * Per-KIND fallback specs. A widget with no per-widget `size` override uses the
 * spec for its kind. Tables are always full-width (4 wide) and tall enough to
 * show several rows; tiles are small (1 wide, one row); charts sit between and
 * default to a square so a chart that fills its card is never cramped. These are
 * starting points only — the user is free to resize from any default.
 */
export const WIDGET_KIND_SPECS: Record<WidgetKind, WidgetSizeSpec> = {
    tile: { default: { w: 1, h: 1 } },
    chart: { default: { w: 2, h: 2 } },
    table: { default: { w: 4, h: 3 } },
};

/**
 * The legacy per-kind defaults (pre KPI widget-sizing). A pin still saved at one
 * of these footprints was never deliberately resized — it took the old default —
 * so `resolveSize` re-fits it to the current spec default, which is how an
 * already-pinned widget picks up the improved sizes without resetting layouts a
 * user has actually customised. Hard-coded (not derived from the specs) so it
 * stays a stable record of what "the old default" was.
 */
const LEGACY_KIND_DEFAULTS: Record<WidgetKind, WidgetSize> = {
    tile: { w: 1, h: 1 },
    chart: { w: 2, h: 1 },
    table: { w: 4, h: 2 },
};

/**
 * Fold a widget's optional per-widget `size` override over its kind fallback.
 * The catalogue declares an override only where the archetype default is wrong
 * for that specific widget (a donut wants a square, etc.); everything else uses
 * its kind spec.
 */
export function sizeSpecFor(kind: WidgetKind, override?: WidgetSizeSpec): WidgetSizeSpec {
    return override ?? WIDGET_KIND_SPECS[kind];
}

/** The default footprint for a kind (no per-widget override). */
export function defaultSizeForKind(kind: WidgetKind): WidgetSize {
    return WIDGET_KIND_SPECS[kind].default;
}

function clamp(n: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, n));
}

/**
 * Clamp a free-form footprint to the grid bounds. The corner-resize grip
 * produces raw (often fractional) cell counts from the pointer delta; this rounds
 * them and pins them inside the global `[MIN_W..MAX_W] × [MIN_H..MAX_H]` range,
 * narrowed by a spec's own `min`/`max` when it declares them. Pure — the resize
 * grip and the context's `setSize` both gate through it.
 */
export function clampSize(w: number, h: number, spec?: WidgetSizeSpec): WidgetSize {
    const loW = spec?.min?.w ?? MIN_W;
    const hiW = spec?.max?.w ?? MAX_W;
    const loH = spec?.min?.h ?? MIN_H;
    const hiH = spec?.max?.h ?? MAX_H;
    return {
        w: clamp(Math.round(w), loW, hiW) as WidgetWidth,
        h: clamp(Math.round(h), loH, hiH) as WidgetHeight,
    };
}

// ─── Static Tailwind span-class maps ────────────────────────────────────────
//
// The canvas is `grid-cols-1 lg:grid-cols-4`: a single column on small screens
// (every widget full-width) and four columns from `lg` up, where the footprint
// applies. Width maps to a column span at `lg`; height maps to a row span at
// `lg` (row packing only matters once the grid is multi-column). Each value is
// a full literal class string so Tailwind's content scanner sees it.

const COL_SPAN_CLASS: Record<WidgetWidth, string> = {
    1: 'lg:col-span-1',
    2: 'lg:col-span-2',
    3: 'lg:col-span-3',
    4: 'lg:col-span-4',
};

const ROW_SPAN_CLASS: Record<WidgetHeight, string> = {
    1: 'lg:row-span-1',
    2: 'lg:row-span-2',
    3: 'lg:row-span-3',
    4: 'lg:row-span-4',
    5: 'lg:row-span-5',
    6: 'lg:row-span-6',
};

/**
 * The Tailwind span classes for a footprint: a full-width base (small screens
 * stack every widget) plus the `lg` column + row spans. Returned as a single
 * space-joined string ready to drop on the grid cell's `className`.
 */
export function spanClasses(size: WidgetSize): string {
    return `col-span-1 ${COL_SPAN_CLASS[size.w]} ${ROW_SPAN_CLASS[size.h]}`;
}

// ─── Static Tailwind placement-class maps (Android-style absolute grid) ──────
//
// A widget's `(x, y)` cell position maps to `col-start` / `row-start` AT `lg`
// (below `lg` the grid is one column and every widget stacks full-width, so
// start positions don't apply). Full literals so the JIT scanner keeps them;
// `row-start` 8–12 are added to the Tailwind theme (col-start 1–4 ship by
// default). Keyed by the 1-based position values the grid ever emits.

const COL_START_CLASS: Record<number, string> = {
    1: 'lg:col-start-1',
    2: 'lg:col-start-2',
    3: 'lg:col-start-3',
    4: 'lg:col-start-4',
};

const ROW_START_CLASS: Record<number, string> = {
    1: 'lg:row-start-1',
    2: 'lg:row-start-2',
    3: 'lg:row-start-3',
    4: 'lg:row-start-4',
    5: 'lg:row-start-5',
    6: 'lg:row-start-6',
    7: 'lg:row-start-7',
    8: 'lg:row-start-8',
    9: 'lg:row-start-9',
    10: 'lg:row-start-10',
    11: 'lg:row-start-11',
    12: 'lg:row-start-12',
};

/**
 * The Tailwind classes that place AND size a widget at cell `(x, y)` on the
 * absolute grid: the full-width mobile base, the `lg` column/row start, and the
 * `lg` column/row spans. `x`/`y` are clamped to the grid so an out-of-range
 * stored position can never emit a missing class.
 */
export function placementClasses(x: number, y: number, size: WidgetSize): string {
    const col = COL_START_CLASS[clamp(x, 1, MAX_W)];
    const row = ROW_START_CLASS[clamp(y, 1, MAX_ROWS)];
    return `col-span-1 ${col} ${COL_SPAN_CLASS[size.w]} ${row} ${ROW_SPAN_CLASS[size.h]}`;
}

// ─── Legacy coercion ────────────────────────────────────────────────────────

/**
 * A persisted pin entry. The pre-#215 wire shape was a bare id `string`; the
 * shape then carried an optional footprint `{ id, w?, h? }`, and now an optional
 * absolute position `{ x?, y? }` too (jsonb holds all — no SQL migration). A
 * stored entry may omit `w`/`h` (resolve to the spec default at render) and/or
 * `x`/`y` (auto-placed into the first free cell at load — `resolveLayout`).
 */
export interface StoredWidgetEntry {
    id: string;
    w?: WidgetWidth;
    h?: WidgetHeight;
    /** 1-based column of the widget's top-left cell on the absolute grid. */
    x?: number;
    /** 1-based row of the widget's top-left cell on the absolute grid. */
    y?: number;
}

/** The legacy or current persisted entry shape, as it arrives off the wire. */
export type LegacyOrSizedEntry = string | StoredWidgetEntry;

/**
 * Normalise one persisted entry to the object shape. A bare id string (legacy
 * `string[]` layout) becomes `{ id }`; an object passes through. The `w`/`h` and
 * `x`/`y` it carries (if any) are preserved; a missing footprint resolves to the
 * spec default at render and a missing position is auto-placed at load. Back-
 * compatibility for existing saved layouts (PRD #185) turns on this being total
 * over both shapes.
 */
export function coerceLegacy(entry: LegacyOrSizedEntry): StoredWidgetEntry {
    if (typeof entry === 'string') return { id: entry };
    return { id: entry.id, w: entry.w, h: entry.h, x: entry.x, y: entry.y };
}

/**
 * Resolve a stored entry to a concrete footprint. This is the render-time
 * resolution the grid runs once it knows the widget's kind + spec:
 *
 *   - A dimension the entry OMITS falls back to the spec default.
 *   - A pin whose FULL stored footprint equals the LEGACY kind default is
 *     re-fitted to the spec default — it took the old default and was never
 *     deliberately sized, so it adopts the improved size (KPI widget-sizing).
 *   - Any other stored footprint (a size the user actually chose) is honoured.
 */
export function resolveSize(
    entry: StoredWidgetEntry,
    spec: WidgetSizeSpec,
    kind: WidgetKind,
): WidgetSize {
    const def = spec.default;
    if (entry.w !== undefined && entry.h !== undefined) {
        const legacy = LEGACY_KIND_DEFAULTS[kind];
        if (entry.w === legacy.w && entry.h === legacy.h) return def;
        return { w: entry.w, h: entry.h };
    }
    return {
        w: entry.w ?? def.w,
        h: entry.h ?? def.h,
    };
}
